import React, { useState, useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, XMarkIcon, QuestionMarkCircleIcon, ArrowRightStartOnRectangleIcon, UserCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import InlineFeedback from './InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import { ReactComponent as Logo } from '../logo.svg';
import { LegalModal } from './LegalModal';
import { Loader } from '@mantine/core';
import { useConverterFeatures } from '../hooks/useFeatureStatus';
import AppSidebar from './layout/AppSidebar';
import { LayoutActionsContext } from './layout/LayoutActionsContext';
import { INSTANCE_INFO_CHANGED } from '../utils/instanceEvents';

interface LayoutProps {
  children: React.ReactNode;
  fullWidthContent?: boolean;
  navigationMode?: 'full' | 'home-only';
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';
const NARROW_QUERY = '(max-width: 1023px)';

const Layout: React.FC<LayoutProps> = ({ children, fullWidthContent = false, navigationMode = 'full' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Re-render when a page reports stored instance info changed (save/rename),
  // so the per-render read below picks up the fresh value
  const [, bumpInstanceVersion] = useReducer((v: number) => v + 1, 0);
  useEffect(() => {
    const onChange = () => bumpInstanceVersion();
    window.addEventListener(INSTANCE_INFO_CHANGED, onChange);
    return () => window.removeEventListener(INSTANCE_INFO_CHANGED, onChange);
  }, []);
  const instanceInfo = apiService.getInstanceInfo();
  const { web: converterWeb, api: converterApi, isLoading: converterLoading } = useConverterFeatures();
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [contactType, setContactType] = useState<'converter' | 'help'>('converter');
  const [contactForm, setContactForm] = useState({ email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const contactFeedback = useFeedback(0);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState<'terms' | 'privacy'>('terms');

  // Sidebar collapse: user preference, forced collapsed on narrow screens
  const [userCollapsed, setUserCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);

  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const sidebarCollapsed = isNarrow || userCollapsed;
  const toggleSidebar = () => {
    setUserCollapsed((prev) => {
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, prev ? '0' : '1');
      } catch {
        // Preference just won't persist
      }
      return !prev;
    });
  };

  // Exit dialog state
  const [saveEmail, setSaveEmail] = useState('');
  const [saveName, setSaveName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const exitFeedback = useFeedback(0);

  // Auto-open exit dialog if triggered from magic link verify page
  useEffect(() => {
    if (localStorage.getItem('showExitDialog') === 'true') {
      localStorage.removeItem('showExitDialog');
      const info = apiService.getInstanceInfo();
      if (info) {
        setSaveEmail(info.email || '');
        setShowExitDialog(true);
      }
    }
  }, []);

  // Open contact dialog if a gated route redirected here.
  useEffect(() => {
    const state = location.state as { openContact?: 'converter' | 'help' } | null;
    if (state?.openContact) {
      setContactType(state.openContact);
      setShowContactPopup(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // Feature flag for converter demo button
  const showConverterDemo = process.env.REACT_APP_SHOW_CONVERTER_DEMO === 'true';

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    contactFeedback.clear();

    try {
      await apiService.submitContactRequest({
        request_type: contactType,
        ...contactForm
      });
      setContactSubmitted(true);
      setContactForm({ email: '', note: '' });
    } catch (error) {
      contactFeedback.showError('Failed to send request. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openContactPopup = (type: 'converter' | 'help') => {
    setContactType(type);
    setContactSubmitted(false);
    contactFeedback.clear();
    setShowContactPopup(true);
  };

  const closeContactPopup = () => {
    setShowContactPopup(false);
    setContactForm({ email: '', note: '' });
    setContactSubmitted(false);
    contactFeedback.clear();
  };

  const openLegalModal = (type: 'terms' | 'privacy') => {
    setLegalModalType(type);
    setLegalModalOpen(true);
  };

  const openExitDialog = () => {
    // Read fresh info: the stored email/mode may have changed since last render
    setSaveEmail(apiService.getInstanceInfo()?.email || '');
    exitFeedback.clear();
    setShowExitDialog(true);
  };

  const closeExitDialog = () => {
    setShowExitDialog(false);
    setSaveEmail('');
    setSaveName('');
    exitFeedback.clear();
  };

  const contentFrame = (
    <main className="flex-1">
      <div data-testid="page-content" className={`py-8 ${fullWidthContent ? 'w-full px-0' : 'max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8'}`}>
        {children}
      </div>
    </main>
  );

  const modals = (
    <>
      {/* Contact Us Popup */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-heading">
                {contactType === 'converter' ? 'Contact Us for Converter' : 'Get Help'}
              </h2>
              <button
                onClick={closeContactPopup}
                className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-body-color mb-6">
              {contactType === 'converter'
                ? "The Alchemist Converter is an enterprise feature. It converts your legacy SAS code to modern platforms like Python and Spark."
                : "Tell us what you need. We will reply soon."
              }
            </p>
            {contactSubmitted ? (
              <div className="space-y-4">
                <InlineFeedback
                  feedback={{ kind: 'success', message: 'We got your request. We will contact you soon.' }}
                />
                <button onClick={closeContactPopup} className="w-full btn-primary-full py-3">
                  Done
                </button>
              </div>
            ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-secondary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label htmlFor="note" className="block text-sm text-secondary mb-2">
                  Message
                </label>
                <textarea
                  id="note"
                  rows={4}
                  required
                  value={contactForm.note}
                  onChange={(e) => setContactForm({ ...contactForm, note: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors resize-none"
                  placeholder={contactType === 'converter'
                    ? "Tell us about your project and requirements..."
                    : "What do you need help with?"
                  }
                />
              </div>
              <InlineFeedback feedback={contactFeedback.feedback} />
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-primary-full py-3"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={closeContactPopup}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {/* Exit Confirmation Dialog */}
      {showExitDialog && instanceInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-heading">
                {instanceInfo.instanceMode === 'persistent' ? 'Sign out' : 'Exit Confirmation'}
              </h2>
              <button
                onClick={closeExitDialog}
                className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <InlineFeedback feedback={exitFeedback.feedback} className="mb-4" />

            {instanceInfo.instanceMode === 'temp' ? (
              // TEMP instance dialog
              <>
                <p className="text-body-color mb-6">
                  This deletes your unsaved instance.
                </p>

                <div className="bg-body-bg border border-secondary rounded-lg p-4 mb-6">
                  <h3 className="text-heading font-medium mb-3 flex items-center gap-2">
                    <EnvelopeIcon className="w-5 h-5" />
                    Want to come back later?
                  </h3>
                  <p className="text-sm text-body-color/70 mb-4">
                    Save your instance. We send a confirmation link to your email. Open it to finish. Then sign in from any device.
                  </p>
                  <form
                    aria-label="Save instance"
                    className="space-y-3"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!saveEmail || !saveEmail.includes('@')) {
                        exitFeedback.showError('Enter a valid email address.');
                        return;
                      }
                      setIsSaving(true);
                      exitFeedback.clear();
                      try {
                        const result = await apiService.saveInstance(saveEmail, saveName || undefined);
                        exitFeedback.showSuccess(`We sent a confirmation link to ${result.pending_email}. Open it in this browser to finish saving.`);
                        setSaveEmail('');
                        setSaveName('');
                      } catch (error: any) {
                        exitFeedback.showError(error.response?.data?.message || 'Failed to save instance.');
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    <input
                      type="email"
                      required
                      aria-label="Email address"
                      placeholder="Email address"
                      value={saveEmail}
                      onChange={(e) => setSaveEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-widget-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary"
                    />
                    <input
                      type="text"
                      aria-label="Instance name (optional)"
                      placeholder="Instance name (optional)"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      className="w-full px-3 py-2 bg-widget-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary"
                    />
                    <button
                      type="submit"
                      disabled={isSaving || !saveEmail.trim()}
                      className="w-full btn-primary-full py-2 flex items-center justify-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <Loader size="xs" color="white" />
                          Saving...
                        </>
                      ) : (
                        'Save Instance'
                      )}
                    </button>
                  </form>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={async () => {
                      setIsDeleting(true);
                      exitFeedback.clear();
                      try {
                        await apiService.deleteInstance();
                      } catch (error: any) {
                        exitFeedback.showError(error.response?.data?.message || 'Failed to delete instance.');
                        setIsDeleting(false);
                        return;
                      }
                      apiService.clearRegistration();
                      window.location.reload();
                    }}
                    disabled={isDeleting}
                    className="flex-1 btn-danger font-medium flex items-center justify-center gap-2"
                  >
                    {isDeleting ? <Loader size="xs" color="white" /> : 'Delete Instance'}
                  </button>
                  <button
                    onClick={closeExitDialog}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              // PERSISTENT instance dialog
              <>
                <p className="text-body-color mb-6">
                  Sign in with your email to open this instance again.
                </p>

                <div className="flex gap-4">
                  <button
                    onClick={() => {
                      apiService.clearRegistration();
                      window.location.reload();
                    }}
                    className="flex-1 btn-primary-full py-3"
                  >
                    Sign out
                  </button>
                  <button
                    onClick={closeExitDialog}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Legal Modal */}
      <LegalModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        type={legalModalType}
      />
    </>
  );

  // Minimal shell for shared/public pages: top bar with Home, small footer, no sidebar
  if (navigationMode === 'home-only') {
    return (
      <LayoutActionsContext.Provider value={{ openExitDialog }}>
      <div className="min-h-screen bg-body-bg flex flex-col">
        <nav className="bg-widget-bg border-b border-secondary">
          <div className={fullWidthContent ? "px-4 sm:px-6 lg:px-8" : "max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8"}>
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <Logo className="h-6 w-auto mr-8" style={{ color: '#F8F9FA' }} />
                <button
                  onClick={() => window.location.href = '/'}
                  className="btn-subtle"
                >
                  <HomeIcon className="w-4 h-4" />
                  Home
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <a
                  href="https://app.getalchemist.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-subtle"
                >
                  <UserCircleIcon className="w-5 h-5" />
                  Portal
                </a>
                <button
                  onClick={() => openContactPopup('help')}
                  className="btn-subtle"
                >
                  <QuestionMarkCircleIcon className="w-5 h-5" />
                  Help
                </button>
              </div>
            </div>
          </div>
        </nav>
        {contentFrame}
        <footer className="border-t border-secondary mt-auto">
          <div className={fullWidthContent ? "px-4 sm:px-6 lg:px-8 py-6" : "max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8 py-6"}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-6">
                <p className="text-sm text-body-color/70">
                  Made by{' '}
                  <a
                    href="https://t1a.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary-hover transition-colors"
                    style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800 }}
                  >
                    T1A
                  </a>
                </p>
                <div className="flex items-center gap-4 text-sm text-body-color/70">
                  <button
                    onClick={() => openLegalModal('privacy')}
                    className="hover:text-primary transition-colors"
                  >
                    Privacy Policy
                  </button>
                  <span className="text-body-color/30">|</span>
                  <button
                    onClick={() => openLegalModal('terms')}
                    className="hover:text-primary transition-colors"
                  >
                    Terms of Use
                  </button>
                </div>
              </div>
              {instanceInfo && (
                <button
                  onClick={openExitDialog}
                  className="btn-subtle flex items-center gap-2 text-sm hover:bg-red-500/10 hover:text-red-500"
                >
                  <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
                  {instanceInfo.instanceMode === 'persistent' ? 'Sign out' : 'Exit'}
                </button>
              )}
            </div>
          </div>
        </footer>
        {modals}
      </div>
      </LayoutActionsContext.Provider>
    );
  }

  // Full app shell: sidebar owns all navigation and utilities
  return (
    <LayoutActionsContext.Provider value={{ openExitDialog }}>
    <div className="min-h-screen bg-body-bg flex">
      <AppSidebar
        collapsed={sidebarCollapsed}
        canToggle={!isNarrow}
        onToggle={toggleSidebar}
        converterState={converterLoading ? 'loading' : converterWeb || converterApi ? 'enabled' : 'gated'}
        onConverterContact={() => openContactPopup('converter')}
        showConverterDemo={showConverterDemo}
        showUsage={instanceInfo?.instanceMode === 'persistent'}
        instanceName={instanceInfo ? instanceInfo.name || 'Instance' : null}
        onHelp={() => openContactPopup('help')}
        onOpenLegal={openLegalModal}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        {contentFrame}
      </div>
      {modals}
    </div>
    </LayoutActionsContext.Provider>
  );
};

export default Layout;
